import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Table } from 'react-bootstrap';
import { documentAPI } from '../../../services/documentAPI';
import { platformAPI } from '../../../services/platformAPI';
import AiAssistNote from '../../../components/workspace/AiAssistNote';
import { uploadTenderDocument } from './documentUpload';
import { AdminPickCell, AdminPlainBar, AdminRowDelete, useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';

const CLASSIFICATIONS = [
  ['unclassified', 'Unclassified'],
  ['standard', 'Standard'],
  ['commercial_confidential', 'Commercial confidential'],
  ['restricted', 'Restricted']
];

const fileNameOf = (doc) => doc.storage?.originalName || doc.originalName || doc.name || 'File';

const TenderDocumentsTab = ({ tenderId, uploadFailures = [], uploadNotices = [] }) => {
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [error, setError] = useState('');
  const [failures, setFailures] = useState(uploadFailures);
  const [notices, setNotices] = useState(uploadNotices);
  const [uploading, setUploading] = useState(false);
  const [typeCode, setTypeCode] = useState('');
  const [classification, setClassification] = useState('standard');
  const [versionOf, setVersionOf] = useState('');
  const [summarisingId, setSummarisingId] = useState('');
  const [aiNote, setAiNote] = useState(null);

  const typeLabel = useMemo(() => {
    const map = new Map(types.map((row) => [row.code, row.label]));
    return (doc) => map.get(doc.typeCode) || map.get(String(doc.type || '').toLowerCase()) || doc.typeCode || doc.type || 'Other';
  }, [types]);

  const load = () => {
    documentAPI.getDocuments({ tenderId, limit: 100 }).then((response) => {
      setDocs(response.data?.data?.documents || response.data?.documents || []);
    }).catch((err) => {
      setError(err.response?.data?.message || 'Documents could not be loaded');
    });
  };

  useEffect(() => { load(); }, [tenderId]);

  const admin = useAdminRecordDelete({
    entity: 'document',
    noun: 'document',
    plural: 'documents',
    reload: load,
    label: (row) => fileNameOf(row)
  });

  useEffect(() => {
    const keep = docs.map((doc) => doc.typeCode).filter(Boolean).join(',');
    platformAPI.listReference('document-types', { area: 'tender_documents', keep }).then((response) => {
      const values = response.data?.data?.values || [];
      setTypes(values.map((row) => ({ code: row.code, label: row.status === 'inactive' ? `${row.label} (inactive)` : row.label, status: row.status })));
      setTypeCode((current) => current || values.find((row) => row.status === 'active')?.code || '');
    }).catch((err) => setError(err.response?.data?.message || 'Document types could not be loaded'));
  }, [docs]);

  const uploadFiles = async (fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    setUploading(true);
    setError('');
    const nextFailures = [];
    const nextNotices = [];
    for (const file of chosen) {
      try {
        const result = await uploadTenderDocument(file, { tenderId, typeCode, accessClassification: classification, versionOf, documentArea: 'tender_documents' });
        if (result.skipped) nextNotices.push({ name: file.name, message: result.message });
        else if (result.message && versionOf) nextNotices.push({ name: file.name, message: result.message });
      } catch (err) {
        nextFailures.push({ name: file.name, message: err.response?.data?.message || 'Upload failed' });
      }
    }
    setFailures(nextFailures);
    setNotices(nextNotices);
    if (!nextFailures.length) setVersionOf('');
    setUploading(false);
    load();
  };

  const extract = async (id) => {
    setError('');
    try {
      await documentAPI.processDocumentAI(id);
      load();
      const status = await platformAPI.getDocumentProcessing(id);
      const detail = status.data?.data;
      if (!detail?.isProcessed && detail?.processingError) setError(detail.processingError);
    } catch (err) {
      setError(err.response?.data?.message || 'Extraction failed');
      load();
    }
  };

  const summarise = async (doc) => {
    setError('');
    setAiNote(null);
    setSummarisingId(doc._id);
    try {
      const response = await platformAPI.summariseDocument(doc._id);
      const data = response.data?.data || {};
      setAiNote(data);
      if (data.summary) {
        setDocs((rows) => rows.map((row) => (
          String(row._id) === String(doc._id)
            ? { ...row, aiExtraction: { ...row.aiExtraction, summary: data.summary, summarySource: 'model' } }
            : row
        )));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Summary was not created');
    } finally {
      setSummarisingId('');
    }
  };

  const classify = async (doc, accessClassification, aiContextEligible) => {
    try {
      await platformAPI.classifyDocument(doc._id, {
        accessClassification,
        tenderId,
        aiContextEligible: accessClassification === 'standard' && aiContextEligible
      });
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Classification failed');
    }
  };

  const download = async (doc) => {
    try {
      const response = await documentAPI.downloadDocument(doc._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileNameOf(doc);
      link.click();
    } catch (err) {
      setError(err.response?.data?.message || 'Download denied');
    }
  };

  return (
    <div>
      <p className="text-muted small">Files use the same document store as corrigenda and the other tender records. Permissions and AI eligibility stay on each file.</p>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      <AiAssistNote result={aiNote} />
      {failures.length ? (
        <Alert variant="warning">
          <div className="fw-semibold mb-1">These files were not stored. The tender itself was kept. Retry them here; a successful file is not created again.</div>
          <ul className="mb-0">
            {failures.map((row) => <li key={`${row.name}-failed`}>{row.name}: {row.message}</li>)}
          </ul>
        </Alert>
      ) : null}
      {notices.length ? (
        <Alert variant="info">
          <ul className="mb-0">
            {notices.map((row) => <li key={`${row.name}-notice`}>{row.name}: {row.message}</li>)}
          </ul>
        </Alert>
      ) : null}
      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <Form.Label>Document type</Form.Label>
          <Form.Select value={typeCode} onChange={(event) => setTypeCode(event.target.value)} aria-label="Document type">
            {types.filter((row) => row.status !== 'inactive' || row.code === typeCode).map((row) => <option key={row.code} value={row.code}>{row.label}</option>)}
          </Form.Select>
        </div>
        <div className="col-md-3">
          <Form.Label>Classification</Form.Label>
          <Form.Select value={classification} onChange={(event) => setClassification(event.target.value)} aria-label="Classification">
            {CLASSIFICATIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Form.Select>
        </div>
        <div className="col-md-3">
          <Form.Label>Store as</Form.Label>
          <Form.Select value={versionOf} onChange={(event) => setVersionOf(event.target.value)} aria-label="New document or new version">
            <option value="">New document</option>
            {docs.map((doc) => <option key={doc._id} value={doc._id}>New version of {fileNameOf(doc)} (v{doc.version || 1})</option>)}
          </Form.Select>
        </div>
        <div className="col-md-3">
          <Form.Label>Upload</Form.Label>
          <Form.Control type="file" multiple={!versionOf} disabled={uploading} onChange={(event) => { uploadFiles(event.target.files); event.target.value = ''; }} />
        </div>
      </div>
      {admin.notice}
      <AdminPlainBar admin={admin} rows={docs} />
      <Table size="sm" responsive>
        <thead>
          <tr>
            {admin.enabled ? <th /> : null}
            <th>Type</th>
            <th>Filename</th>
            <th>Version</th>
            <th>Status</th>
            <th>Uploaded</th>
            <th>Classification</th>
            <th>AI</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {docs.map((doc) => {
            const extracted = Boolean(doc.aiExtraction?.isProcessed);
            const access = doc.accessClassification || 'unclassified';
            return (
              <tr key={doc._id}>
                <AdminPickCell admin={admin} id={doc._id} />
                <td>{typeLabel(doc)}</td>
                <td>
                  {fileNameOf(doc)}
                  {doc.withheld ? <div className="text-muted small">Withheld</div> : null}
                  {doc.aiExtraction?.summarySource === 'model' && doc.aiExtraction?.summary ? (
                    <div className="text-muted small">{doc.aiExtraction.summary}</div>
                  ) : null}
                </td>
                <td>{doc.version || 1}</td>
                <td>
                  {doc.status || 'UPLOADED'}
                  {extracted ? <div className="text-muted small">Text extracted</div> : null}
                  {doc.aiExtraction?.processingError ? <div className="text-danger small">{doc.aiExtraction.processingError}</div> : null}
                </td>
                <td>
                  {doc.uploadedBy?.name || '—'}
                  <div className="text-muted small">{doc.createdAt ? new Date(doc.createdAt).toLocaleString('en-GB') : ''}</div>
                </td>
                <td>
                  <Form.Select size="sm" value={access} onChange={(event) => classify(doc, event.target.value, doc.aiContextEligible)} aria-label={`Classification for ${fileNameOf(doc)}`}>
                    {CLASSIFICATIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </Form.Select>
                </td>
                <td>
                  <Form.Check
                    type="checkbox"
                    label="Eligible"
                    checked={Boolean(doc.aiContextEligible)}
                    disabled={access !== 'standard'}
                    onChange={(event) => classify(doc, access, event.target.checked)}
                  />
                </td>
                <td className="text-end text-nowrap">
                  <Button size="sm" variant="outline-primary" className="me-1" onClick={() => extract(doc._id)}>Extract text</Button>
                  <Button size="sm" variant="outline-primary" className="me-1" disabled={summarisingId === doc._id} onClick={() => summarise(doc)}>
                    {summarisingId === doc._id ? 'Summarising…' : 'Summarise'}
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => download(doc)}>Download</Button>
                  <AdminRowDelete admin={admin} id={doc._id} label={fileNameOf(doc)} />
                </td>
              </tr>
            );
          })}
          {!docs.length ? <tr><td colSpan={admin.enabled ? 9 : 8}>No documents on this tender yet.</td></tr> : null}
        </tbody>
      </Table>
      {admin.modal}
    </div>
  );
};

export default TenderDocumentsTab;
